import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import styled, { useTheme } from 'styled-components';
import ArrowRightIcon from '../../assets/icons/arrow-right.svg?react';
import {
  selectTokenByNetworkAndSymbol,
  selectTokensWithUSDBalance,
  selectTotalWalletBalanceUSD,
} from '../../redux/slices/tokenSlice';
import { useSolana } from '../../hooks/useSolana';
import { useNavigate } from 'react-router-dom';
import {
  Heading3Text,
  ParagraphMediumText,
  Heading4Text,
} from '../../components/Texts';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { formatUSD, formatUSDEnsure2Digits } from '../../common/currency';
import PortfolioSkeleton from './PortfolioSkeleton';
import { usePrivy } from '@privy-io/react-auth';
import Rewards from '../../components/Rewards';
import PortfolioEmptyState from './PortfolioEmptyState';
import Button from '../../components/Button';
import { selectLastRefreshTime } from '../../redux/slices/walletSlice';
import { openModal } from '../../redux/slices/modals';
import { Table, TableContainer, Tbody, Td, Tr } from '../../components/Table';
import TrendingTokens from '../Home/TrendingTokens';
import ShopGiftCards from '../Home/ShopGiftCards';
import { IonRefresher, IonRefresherContent } from '@ionic/react';
import { hapticsImpactLight } from '../../common/haptic';
import TokenActions from '../../components/TokenActions';
import {
  useGetCurrentUserProfileQuery,
  useGetCurrentUserQuery,
} from '../../redux/api/authorized/auth';
import Banners from '../../components/Banners';

const BalanceContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.border.primary};
  border-radius: 16px;
  padding: 16px;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  @media (min-width: ${({ theme }) => theme.breakpoints?.phone || '768px'}) {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: fit-content;
  }
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 15px;
    border: unset;
    border-radius: 12px;
    padding-top: 10px;
    padding-bottom: 0px;
    padding-left: 12px;
    padding-right: 12px;
    background: ${({ theme }) => theme.background.secondary};
  }
`;

const OutBalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media (min-width: ${({ theme }) => theme.breakpoints?.phone || '768px'}) {
    flex-direction: row;
    gap: 16px;
  }
`;

const AssetInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Icon = styled.img`
  border-radius: 50%;
  object-fit: cover;
  width: 40px;
`;

const Label = styled.div`
  display: flex;
  flex-direction: column;
`;

const AssetName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
`;

const AssetCode = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text.light};
`;

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: flex-end;
  gap: 8px;
`;

const PortfolioTitle = styled.p`
  font-weight: 500;
  font-size: 20px;
  color: ${({ theme }) => theme.text.title};
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 10px;
    font-size: 20px;
    font-weight: 500;
    color: ${({ theme }) => theme.text.title};
    cursor: pointer;
  }
`;

const ShopGiftCardsWrapper = styled.div`
  display: none;
  @media (min-width: ${({ theme }) => theme.breakpoints?.phone || '768px'}) {
    display: flex;
    width: 40%;
    flex-direction: column;
  }
`;

const formatBalance = (balance, symbol) => {
  if (!balance || balance === 0) return '0';

  const balanceNum = parseFloat(balance);

  // Manejar valores inválidos
  if (isNaN(balanceNum) || !isFinite(balanceNum)) {
    return '0';
  }

  // Si es USDC, siempre 2 decimales
  if (symbol === 'USDC') {
    return balanceNum.toFixed(2);
  }

  // Si la cantidad es mayor a 100, 2 decimales
  if (balanceNum >= 100) {
    return balanceNum.toFixed(2);
  }

  // Si la cantidad es menor a 1, usar 4 dígitos significativos
  if (balanceNum < 1) {
    // Usar toPrecision pero convertir notación científica a decimal si es necesario
    const precisionResult = balanceNum.toPrecision(2);

    // Si el resultado contiene 'e' (notación científica), convertir a decimal
    if (precisionResult.includes('e') || precisionResult.includes('E')) {
      // Convertir notación científica a decimal con hasta 8 decimales
      const decimalStr = balanceNum.toFixed(8);
      // Remover ceros innecesarios al final
      return parseFloat(decimalStr).toString();
    }

    return precisionResult;
  }

  // Si la cantidad es menor a 100 pero mayor o igual a 1, 4 decimales
  return balanceNum.toFixed(4);
};

const Row = ({ asset }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const navigate = useNavigate();
  const isUsdc = asset.symbol === 'USDC';
  const theme = useTheme();

  const formattedBalance = formatBalance(asset.balance, asset.symbol);

  return (
    <Tr
      key={asset.symbol}
      onClick={() => {
        hapticsImpactLight();
        navigate(`/app/tokens/${asset.address}`);
      }}
    >
      <Td>
        <AssetInfo>
          <Icon src={asset.logoURI} />
          <Label>
            <AssetName>{asset.name}</AssetName>
            <AssetCode>{`${formattedBalance} ${asset.symbol}`}</AssetCode>
          </Label>
        </AssetInfo>
      </Td>
      <Td>
        <div>
          <p
            style={{
              color: theme.text.title,
              fontSize: '16px',
              ...(isMobile && {
                fontWeight: 400,
                textAlign: 'right',
                marginRight: '10px',
              }),
            }}
          >
            {formatUSD(asset.usdValue || 0)}
          </p>
        </div>
      </Td>
      {!isMobile && (
        <Td>
          <ButtonContainer>
            <Button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/app/tokens/${asset.address}?tab=trade`);
              }}
              disabled={!!isUsdc}
              style={{ height: 32, opacity: !isUsdc ? '100%' : '0%' }}
              variant='secondary'
            >
              {t('dashboard.trade')}
            </Button>
            <Button
              variant='secondary'
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/app/tokens/${asset.address}?tab=gift`);
              }}
              style={{ height: 32 }}
            >
              {t('dashboard.gift')}
            </Button>
          </ButtonContainer>
        </Td>
      )}
    </Tr>
  );
};

const Container = styled.div`
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 8px;
  }
`;

const LabelAndBalanceContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const BalanceTopContent = styled.div`
  @media (min-width: ${({ theme }) => theme.breakpoints?.phone || '768px'}) {
    display: flex;
    flex-direction: column;
  }
`;

const StyledHeading3Text = styled(Heading3Text)`
  @media ${PHONE_MEDIA_QUERY} {
    font-size: 30px;
    margin-top: 0px;
  }
`;

export default function AssetTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const tokens = useSelector((state) => selectTokensWithUSDBalance(state));
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const totalBalance = useSelector(selectTotalWalletBalanceUSD);

  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, 'solana', 'USDC'),
  );

  const tokensRefreshed = useSelector(selectLastRefreshTime);

  const [isLoading, setIsLoading] = useState(true);

  const { authenticated, ready } = usePrivy();

  const { walletsReady, refreshTokenBalances } = useSolana();

  const handleRefresh = async (event) => {
    await refreshTokenBalances();
    event.detail.complete();
  };

  const { data: currentUser } = useGetCurrentUserProfileQuery();

  const theme = useTheme();

  useEffect(() => {
    if (currentUser?.id && !currentUser?.onboardingCompleted) {
      dispatch(
        openModal({ name: 'giftClaimed', props: { fromDashboard: true } }),
      );
    }
  }, [currentUser]);

  useEffect(() => {
    // Show loading skeleton while we're loading prices or balances
    if (ready && !authenticated) {
      setIsLoading(false);
      return;
    }

    setIsLoading(tokensRefreshed === 0 || !walletsReady);
  }, [ready, authenticated, tokensRefreshed, walletsReady]);

  // Memoize the sorted tokens to prevent unnecessary re-renders
  const sortedTokens = React.useMemo(() => {
    return [...tokens]
      .filter((token) => parseFloat(token.usdValue || 0) > 0) // Filter out tokens with 0 USD value
      .sort((a, b) => {
        const usdValueA = parseFloat(a.usdValue || 0);
        const usdValueB = parseFloat(b.usdValue || 0);
        return usdValueB - usdValueA;
      });
  }, [tokens]);

  const tokensWithBalance = sortedTokens?.length || 0;

  if (isLoading || (authenticated && !walletsReady)) {
    return <PortfolioSkeleton />;
  }

  return (
    <Container>
      {isMobile && (
        <IonRefresher slot='fixed' onIonRefresh={handleRefresh}>
          <IonRefresherContent />
        </IonRefresher>
      )}
      <Banners />
      {authenticated && (
        <OutBalanceContainer>
          <BalanceContainer>
            <BalanceTopContent>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: 10,
                }}
              >
                <LabelAndBalanceContainer>
                  <ParagraphMediumText
                    style={{ fontWeight: 600, color: theme.text.light }}
                  >
                    {t('home.balance')}
                  </ParagraphMediumText>
                  <StyledHeading3Text
                    style={{
                      color: theme.text.title,
                      ...(isMobile && {
                        fontSize: '20px',
                      }),
                    }}
                  >
                    {formatUSD(totalBalance)}
                  </StyledHeading3Text>
                </LabelAndBalanceContainer>
                {isMobile && (
                  <Button
                    onClick={() => {
                      hapticsImpactLight();
                      dispatch(
                        openModal({
                          name: 'addFunds',
                        }),
                      );
                    }}
                    variant='secondary'
                    style={{
                      backgroundColor: theme.background.lighter,
                      marginLeft: 'auto',
                      borderRadius: '15px',
                      fontSize: '12px',
                      height: '30px',
                      width: '80px',
                      marginTop: '10px',
                    }}
                  >
                    {t('home.add')}
                  </Button>
                )}
                {!isMobile && (
                  <div
                    onClick={() => {
                      hapticsImpactLight();
                      navigate('/app/discover');
                    }}
                    style={{
                      color: theme.text.regular,
                      marginLeft: 'auto',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      flexDirection: 'row',
                      gap: 0,
                      color: theme.text.title,
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
                      <p style={{ fontWeight: 500, fontSize: '18px' }}>{t('home.exploreAssets')}</p>
                      <ArrowRightIcon
                        width={15}
                        height={15}
                        style={{ color: 'inherit' }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </BalanceTopContent>
            {!isMobile && (
              <TokenActions
                onGiftClick={() => {
                  navigate('/app/gifts/create');
                }}
                onTradeClick={() => {
                  navigate('/app/trade');
                }}
                onSpendClick={() => {
                  navigate('/app/spend');
                }}
                onSendClick={() => {
                  dispatch(openModal({ name: 'withdrawFunds' }));
                }}
                onReceiveClick={() => {
                  hapticsImpactLight();
                  dispatch(
                    openModal({
                      name: 'addFunds',
                    }),
                  );
                }}
              />
            )}
            {/* {isMobile && (
              <img
                src='/wallet/card.webp'
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  hapticsImpactLight();
                  dispatch(openModal({ name: 'debitCardWaitlist' }));
                }}
              />
            )} */}
          </BalanceContainer>
        </OutBalanceContainer>
      )}
      <ShopGiftCards />
      <TrendingTokens />
      <PortfolioTitle>{t('home.myPortfolio')}</PortfolioTitle>
      {tokensWithBalance === 0 ? (
        <PortfolioEmptyState />
      ) : (
        <TableContainer>
          <Table>
            <Tbody>
              {sortedTokens.map((asset, i) => (
                <Row key={i} i={i} asset={asset} />
              ))}
            </Tbody>
          </Table>
        </TableContainer>
      )}
      {/* <Heading4Text style={{ marginTop: 60, color: theme.text.title }}>
        How to Earn More
      </Heading4Text>
      <Rewards /> */}
    </Container>
  );
}
